<template>
  <div class="mb-64 md:mb-112">
    <div class="layout-grid pt-[105px] text-center md:pt-[150px]">
      <div class="layout-grid-contain">
        <h1
          class="mb-72 text-6xl uppercase md:mb-24 md:text-7xl"
          :class="{
            'font-bold': isRebranding,
          }"
        >
          {{ $texts('worldOfOris', 'World of Oris') }}
        </h1>

        <!-- Mobile menu -->
        <InputDropdown
          v-model="selectedLink"
          :items="links"
          class="w-full md:hidden"
        />

        <!-- Desktop menu -->
        <ul
          class="mx-auto flex list-none flex-wrap justify-center gap-24 uppercase mobile-only:hidden md:max-w-[900px]"
        >
          <li
            v-for="link in links"
            :key="link.key"
            class="relative before:absolute before:-left-12 before:top-[5px] before:hidden before:h-12 before:w-1 before:bg-grey-dark-02 before:md:[&:not(:first-child)]:block"
          >
            <DrupalLink
              :to="link.value"
              class="border-color-black pb-4 text-base text-grey-dark-02 hover:border-b"
              :class="{
                'border-b !text-black': link.value === route.path,
                'border-pink': isRebranding,
                'hover:text-black': !isRebranding,
              }"
            >
              {{ link.labelShort || link.label }}
            </DrupalLink>
          </li>
        </ul>
      </div>
    </div>
    <NuxtPage />
  </div>
</template>

<script lang="ts" setup>
/**
 * This is the root page component for "World of Oris".
 *
 * It renders the stage and provides a <NuxtPage> slot to render either:
 * - the "all" page (defined in ./worldoforis/index.vue)
 * - the canonical page of a magazine category (defined in ./worldoforis/[category]/index.vue)
 */
import { falsy, narrowTypeByProperty } from '~/helpers/type'

const route = useRoute()
const router = useRouter()
const { $texts } = useNuxtApp()
const isRebranding = await useFeatureFlag()

const selectedLink = computed({
  get() {
    return route.path
  },
  set(v: string) {
    router.push(v)
  },
})

const { data: categories } = await useAsyncData(
  () =>
    useCachedGraphqlQuery('magazineStage').then((v) =>
      (v.data.categories.items || [])
        .map((c) => narrowTypeByProperty(c, 'label'))
        .filter(falsy),
    ),
  {
    default: () => {
      return []
    },
  },
)

type StageLink = {
  key: string
  value: string
  label: string
  labelShort?: string
}

const links = computed<StageLink[]>(() => {
  const mappedItems = categories.value
    .map((item) => {
      if (!item.url?.path) {
        return undefined
      }
      return {
        key: item.uuid,
        value: item.url.path,
        label: item.label ?? '',
      }
    })
    .filter(falsy)

  return [
    {
      key: 'all',
      value: router.resolve({ name: 'worldoforis' }).href,
      label: $texts('allCategories', 'All categories'),
      labelShort: $texts('allCategoriesShort', 'All'),
    },
    ...mappedItems,
  ]
})
</script>
